import { IPoint2d } from './IPoint2d';
import * as THREE from 'three';
import { IIntersectInfo, isClockwise } from './utils';
import { Line2d } from './Line2d';
import { LineSeg2d } from './LineSeg2d';
import { Point2dUtils } from './Point2dUtils';
import { effectiveDistance, thresholdValue } from './const';
import { IShapePoint2d } from '../types/shape';
import { dispersePath } from '../utils/shape';

export interface EdgeOffset {
  edgeIndex: number; // 边索引（从0开始）
  distance: number; // 正数为外扩，负数为内缩
}

export class Curve2dUtils {
  static offsetPoints(curve: LineSeg2d, originPoint: IPoint2d, offsetValue: number) {
    const offset = Math.abs(offsetValue);

    const left = Curve2dUtils.offsetPoint(curve, originPoint, -offset);
    const right = Curve2dUtils.offsetPoint(curve, originPoint, offset);

    return { left, right };
  }

  // offsetValue为正数时，向左偏移，为负数时，向右偏移
  static offsetPoint(curve: LineSeg2d, originPoint: IPoint2d, offsetValue: number): IPoint2d {
    // 获取当前线段的向量
    const curveVector = new THREE.Vector2(curve.end.x - curve.start.x, curve.end.y - curve.start.y);
    curveVector.normalize(); // 归一化垂线方向

    // 计算垂线方向（旋转切线90度）
    // 或者使用 perpendicular = tangent.clone().rotateAround(new THREE.Vector2(), Math.PI / 2);
    const perpendicular = new THREE.Vector2(-curveVector.y, curveVector.x);
    perpendicular.normalize(); // 归一化垂线方向

    const offsetVector = perpendicular.multiplyScalar(-offsetValue);

    return Point2dUtils.added(originPoint, offsetVector);
  }

  static offsetCurves(curve: LineSeg2d, offsetValue: number) {
    const offset = Math.abs(offsetValue);
    const leftCurve = Curve2dUtils.offsetCurve(curve, -offset);
    const rightCurve = Curve2dUtils.offsetCurve(curve, offset);

    return { leftCurve, rightCurve };
  }

  static offsetCurve(curve: LineSeg2d, offsetValue: number) {
    const startOffsetPt = Curve2dUtils.offsetPoint(curve, { x: curve.start.x, y: curve.start.y }, offsetValue);
    const endOffsetPt = Curve2dUtils.offsetPoint(curve, { x: curve.end.x, y: curve.end.y }, offsetValue);

    const offsetCuver = new LineSeg2d(startOffsetPt, endOffsetPt);
    return offsetCuver;
  }

  /**
   * 多边形各边独立偏移
   * @param polygon 原始多边形顶点数组
   * @param offsets 各边偏移配置
   * @returns 偏移后的多边形
   */
  static variableOffsetPolygon(polygon: IPoint2d[], offsets: EdgeOffset[]): IPoint2d[] {
    if (polygon.length < 3) return [];

    // 确定多边形方向
    const bClockwise = isClockwise(polygon);
    const directionSign = bClockwise ? -1 : 1;

    // 初始化所有边的偏移量为0
    const offsetVectors = polygon.map((_, i) => {
      const next = (i + 1) % polygon.length;
      const edge = {
        x: polygon[next].x - polygon[i].x,
        y: polygon[next].y - polygon[i].y
      };
      const length = Math.sqrt(edge.x * edge.x + edge.y * edge.y);
      return {
        x: 0,
        y: 0,
        perpX: edge.x,
        perpY: edge.y,
        length
      };
    });

    // 应用指定的边偏移
    for (const offset of offsets) {
      if (offset.edgeIndex >= 0 && offset.edgeIndex < offsetVectors.length) {
        const vec = offsetVectors[offset.edgeIndex];
        if (vec.length > 0) {
          const nx = -vec.perpY / vec.length;
          const ny = vec.perpX / vec.length;
          const effectiveDistance = offset.distance * directionSign;
          vec.x = nx * effectiveDistance;
          vec.y = ny * effectiveDistance;
        }
      }
    }

    // 计算偏移后的顶点
    const newPolygon: IPoint2d[] = [];
    const len = polygon.length;
    for (let i = 0; i < len; i++) {
      const prevIndex = (i - 1 + len) % len;
      const current = polygon[i];
      const prevVector = offsetVectors[prevIndex];
      const currVector = offsetVectors[i];

      // 计算两条偏移边的交点
      const line1 = new Line2d({
        points: {
          p1: { x: current.x + prevVector.x, y: current.y + prevVector.y },
          p2: {
            x: current.x + prevVector.x + prevVector.perpX,
            y: current.y + prevVector.y + prevVector.perpY
          }
        }
      });
      const line2 = new Line2d({
        points: {
          p1: { x: current.x + currVector.x, y: current.y + currVector.y },
          p2: {
            x: current.x + currVector.x + currVector.perpX,
            y: current.y + currVector.y + currVector.perpY
          }
        }
      });
      const intersection = line1.intersection(line2);
      if (!intersection) {
        // 说明平行了
        const insertPoint1 = { x: current.x + prevVector.x, y: current.y + prevVector.y };
        const insertPoint2 = { x: current.x + currVector.x, y: current.y + currVector.y };
        if (Point2dUtils.distanceTo(insertPoint1, insertPoint2) > effectiveDistance) {
          newPolygon.push(insertPoint1, insertPoint2);
        } else {
          // 共线且偏移值一样
          newPolygon.push(insertPoint1);
        }
      } else {
        newPolygon.push(intersection);
      }

      // newPolygon.push(intersection || { x: current.x + prevVector.x, y: current.y + prevVector.y });
    }

    return newPolygon;
  }

  /**
   * 计算多边形的圆心（质心）
   * @param vertices 多边形顶点数组，按顺时针或逆时针顺序排列
   * @returns 圆心坐标
   */
  static calculatePolygonCentroid(_vertices: IShapePoint2d[]): IPoint2d {
    const vertices = dispersePath(_vertices);

    if (vertices.length < 3) {
      throw new Error('多边形至少需要3个顶点');
    }

    let area = 0;
    let centroidX = 0;
    let centroidY = 0;

    // 使用鞋带公式(Shoelace formula)计算质心
    for (let i = 0; i < vertices.length; i++) {
      const current = vertices[i];
      const next = vertices[(i + 1) % vertices.length];

      const crossProduct = current.x * next.y - next.x * current.y;
      area += crossProduct;

      centroidX += (current.x + next.x) * crossProduct;
      centroidY += (current.y + next.y) * crossProduct;
    }

    area /= 2;
    if (Math.abs(area) < 1e-6) {
      // 退化多边形（面积为 0），返回顶点平均值兜底
      return Curve2dUtils.calculateVerticesAverage(vertices);
    }
    const factor = 1 / (6 * area);

    centroidX *= factor;
    centroidY *= factor;

    return { x: centroidX, y: centroidY };
  }

  /**
   * 计算带洞多边形的质心（洞按面积负权重扣除，不依赖环的绕向）
   * @param path 外轮廓离散点
   * @param holes 洞的离散点数组
   */
  static calculatePolygonWithHolesCentroid(path: IPoint2d[], holes: IPoint2d[][] = []): IPoint2d {
    const exterior = Curve2dUtils.calculateRingAreaCentroid(path);

    let totalArea = exterior.area;
    let centroidX = exterior.area * exterior.x;
    let centroidY = exterior.area * exterior.y;

    for (const hole of holes) {
      const ring = Curve2dUtils.calculateRingAreaCentroid(hole);
      totalArea -= ring.area;
      centroidX -= ring.area * ring.x;
      centroidY -= ring.area * ring.y;
    }

    if (totalArea < 1e-6) {
      // 退化多边形（洞占满外轮廓或面积为 0），返回外轮廓顶点平均值兜底
      return Curve2dUtils.calculateVerticesAverage(path);
    }

    return { x: centroidX / totalArea, y: centroidY / totalArea };
  }

  /**
   * 计算单个环的无符号面积和质心（鞋带公式）
   */
  private static calculateRingAreaCentroid(ring: IPoint2d[]): { area: number; x: number; y: number } {
    let area = 0;
    let centroidX = 0;
    let centroidY = 0;

    for (let i = 0; i < ring.length; i++) {
      const current = ring[i];
      const next = ring[(i + 1) % ring.length];

      const crossProduct = current.x * next.y - next.x * current.y;
      area += crossProduct;
      centroidX += (current.x + next.x) * crossProduct;
      centroidY += (current.y + next.y) * crossProduct;
    }

    area /= 2;
    if (Math.abs(area) < 1e-6) {
      const average = Curve2dUtils.calculateVerticesAverage(ring);
      return { area: 0, x: average.x, y: average.y };
    }

    const factor = 1 / (6 * area);
    return { area: Math.abs(area), x: centroidX * factor, y: centroidY * factor };
  }

  /**
   * 计算顶点平均值
   */
  private static calculateVerticesAverage(vertices: IPoint2d[]): IPoint2d {
    if (!vertices.length) {
      return { x: 0, y: 0 };
    }
    let sumX = 0;
    let sumY = 0;
    for (const vertex of vertices) {
      sumX += vertex.x;
      sumY += vertex.y;
    }
    return { x: sumX / vertices.length, y: sumY / vertices.length };
  }

  /**
   * 判断点是否在多边形内部（使用射线法）
   * @param point 要判断的点
   * @param polygon 多边形顶点数组，按顺时针或逆时针顺序排列
   * @returns 是否在多边形内部
   */
  static isPointInPolygon(point: IPoint2d, polygon: IPoint2d[]): boolean {
    if (polygon.length < 3) {
      throw new Error('多边形至少需要3个顶点');
    }

    const x = point.x;
    const y = point.y;
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x;
      const yi = polygon[i].y;
      const xj = polygon[j].x;
      const yj = polygon[j].y;

      // 检查点是否在多边形的顶点上
      if ((xi === x && yi === y) || (xj === x && yj === y)) {
        return true;
      }

      // 检查点是否在边的水平线上
      const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

      if (intersect) {
        inside = !inside;
      }
    }

    return inside;
  }

  /**
   * 判断点是否在多边形上
   * @param point 要判断的点
   * @param polygon 多边形顶点数组，按顺时针或逆时针顺序排列
   * @returns 是否在多边形内部
   */
  static isPointOnPolygon(point: IPoint2d, polygon: IPoint2d[]): boolean {
    const len = polygon.length;
    for (let i = 0; i < len; i++) {
      const seg = new LineSeg2d(polygon[i], polygon[(i + 1) % len]);
      if (seg.pointOnSegment(point)) {
        return true;
      }
    }

    return false;
  }

  /**
   * 判断多边形是否在多边形内部，不包含重合的情况
   */
  static isPolygonInPolygon(insidePolygon: IPoint2d[], polygon: IPoint2d[]): boolean {
    if (polygon.length < 3 || insidePolygon.length < 3) {
      throw new Error('多边形至少需要3个顶点');
    }

    // 先排除重合的情况
    if (Curve2dUtils.isPolygonOnPolygon(insidePolygon, polygon)) return false;

    const insidePolygonSegs = insidePolygon.map(
      (point, i) => new LineSeg2d(point, insidePolygon[(i + 1) % insidePolygon.length])
    );
    const polygonSegs = polygon.map((point, i) => new LineSeg2d(point, polygon[(i + 1) % polygon.length]));

    // 判断点是否在多边形内部/边上
    // 需要再添加下线段中点检测，避免polygon为凹多边形，且insidePolygon的顶点在polygon的边上，导致判断错误
    const insidePolygonWithMiddlePoints = [...insidePolygon];
    for (let i = 0; i < insidePolygon.length; i++) {
      const startPoint = insidePolygon[i];
      const endPoint = insidePolygon[(i + 1) % insidePolygon.length];
      const middlePoint = {
        x: (startPoint.x + endPoint.x) / 2,
        y: (startPoint.y + endPoint.y) / 2
      };
      insidePolygonWithMiddlePoints.push(middlePoint);
    }
    for (const insidePoint of insidePolygonWithMiddlePoints) {
      if (polygonSegs.some((seg) => seg.pointOnSegment(insidePoint))) continue;
      if (Curve2dUtils.isPointInPolygon(insidePoint, polygon)) continue;
      return false;
    }

    // 检查真相交情况（排除端点相交）
    for (const insideSeg of insidePolygonSegs) {
      for (const seg of polygonSegs) {
        if (seg.pointOnSegment(insideSeg.start) || seg.pointOnSegment(insideSeg.end)) continue;
        if (insideSeg.pointOnSegment(seg.start) || insideSeg.pointOnSegment(seg.end)) continue;
        if (insideSeg.intersection(seg)) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * 判断多边形是否在多边形上
   * @param point 要判断的点
   * @param polygon1 多边形顶点数组
   * @param polygon2 多边形顶点数组
   * @returns 是否在多边形内部
   */
  static isPolygonOnPolygon(polygon1: IPoint2d[], polygon2: IPoint2d[]): boolean {
    if (polygon1.length < 3 || polygon2.length < 3) {
      throw new Error('多边形至少需要3个顶点');
    }

    const polygon2Segs = polygon2.map((point, i) => new LineSeg2d(point, polygon2[(i + 1) % polygon2.length]));

    let pointOnPolygon = false;
    for (const point of polygon1) {
      pointOnPolygon = false;
      for (const seg of polygon2Segs) {
        if (seg.pointOnSegment(point)) {
          pointOnPolygon = true;
          break;
        }
      }

      if (!pointOnPolygon) return false;
    }

    const polygon1Segs = polygon1.map((point, i) => new LineSeg2d(point, polygon1[(i + 1) % polygon1.length]));
    for (const point of polygon2) {
      pointOnPolygon = false;
      for (const seg of polygon1Segs) {
        if (seg.pointOnSegment(point)) {
          pointOnPolygon = true;
          break;
        }
      }

      if (!pointOnPolygon) return false;
    }

    return true;
  }

  /**
   * 判断线段是否在多边形内部（支持带孔洞的多边形）
   * @param seg 待判断的线段
   * @param polygon 多边形，polygon[0]为外边界，polygon[1..n]为孔洞
   * @param strict true 严格模式，线段及端点必须在多边形内部（不可在边上）；
   *               false 非严格模式，线段端点可以在多边形边上
   * @param allowOverlap 是否允许线段与多边形边重叠，strict为false时，该参数才有效
   * @returns 线段是否在多边形内部
   */
  static isLineSegInPolygon(
    seg: LineSeg2d,
    polygon: IPoint2d[][],
    strict: boolean = false,
    allowOverlap: boolean = false
  ): boolean {
    if (polygon.length === 0 || polygon[0].length < 3) return false;

    const outerRing = polygon[0];
    const holes = polygon.slice(1);

    /**
     * 判断点是否在多边形有效区域内
     * strict模式：点必须严格在内部（不可在外边界或孔洞边界上）
     * 非strict模式：点可以在外边界或孔洞边界上，但不可在孔洞内部
     */
    const isPointValid = (point: IPoint2d): boolean => {
      const onOuterEdge = Curve2dUtils.isPointOnPolygon(point, outerRing);
      const inOuter = onOuterEdge || Curve2dUtils.isPointInPolygon(point, outerRing);
      if (!inOuter) return false;
      if (strict && onOuterEdge) return false;
      for (const hole of holes) {
        if (hole.length < 3) continue;
        const onHoleEdge = Curve2dUtils.isPointOnPolygon(point, hole);
        if (strict && onHoleEdge) return false;
        // 在孔洞内部（非边界）则无效
        if (!onHoleEdge && Curve2dUtils.isPointInPolygon(point, hole)) return false;
      }
      return true;
    };

    // 1. 验证线段两端点
    if (!isPointValid(seg.start) || !isPointValid(seg.end)) return false;

    // 2. 收集所有多边形边（外边界 + 孔洞）
    const allEdges: LineSeg2d[] = [];
    for (const ring of polygon) {
      for (let i = 0; i < ring.length; i++) {
        allEdges.push(new LineSeg2d(ring[i], ring[(i + 1) % ring.length]));
      }
    }

    // 3. 检查线段与多边形边的交点
    //    - 共线重叠检查：线段不能与多边形边重叠（平行且有重合部分）
    //    - crossPoint 返回非端点处的真相交 → 说明线段穿越了多边形边界，直接返回 false
    //    - intersection 返回所有交点（含端点接触），记录其在线段上的参数位置用于后续子段检查
    const params: number[] = [0, 1];
    for (const edge of allEdges) {
      if (!allowOverlap && seg.isCollinear(edge) && seg.getOverlapSegment(edge)) return false;
      if (seg.crossPoint(edge)) return false;
      const pt = seg.intersection(edge);
      if (pt) {
        params.push(seg.getPointParameter(pt));
      }
    }

    // 4. 按参数排序后，检查每个子段的中点是否在多边形内
    //    用于处理凹多边形中线段经过顶点后可能穿出多边形的情况
    params.sort((a, b) => a - b);

    for (let i = 0; i < params.length - 1; i++) {
      if (params[i + 1] - params[i] < thresholdValue) continue;
      const midT = (params[i] + params[i + 1]) / 2;
      const midPoint = seg.getPointFromParameter(midT);
      if (!isPointValid(midPoint)) return false;
    }

    return true;
  }

  /**
   * 计算点到多边形边的最小距离
   */
  static pointToPolygonDistance(point: IPoint2d, polygon: IPoint2d[]): number {
    let minDistance = Infinity;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const segmentStart = polygon[j];
      const segmentEnd = polygon[i];

      const distance = Curve2dUtils.pointToSegmentDistance(point, segmentStart, segmentEnd);
      if (distance < minDistance) {
        minDistance = distance;
      }
    }

    return minDistance;
  }

  /**
   * 计算点到线段的距离
   */
  static pointToSegmentDistance(point: IPoint2d, segmentStart: IPoint2d, segmentEnd: IPoint2d): number {
    const A = point.x - segmentStart.x;
    const B = point.y - segmentStart.y;
    const C = segmentEnd.x - segmentStart.x;
    const D = segmentEnd.y - segmentStart.y;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;

    if (lenSq !== 0) {
      param = dot / lenSq;
    }

    let xx, yy;

    if (param < 0) {
      xx = segmentStart.x;
      yy = segmentStart.y;
    } else if (param > 1) {
      xx = segmentEnd.x;
      yy = segmentEnd.y;
    } else {
      xx = segmentStart.x + param * C;
      yy = segmentStart.y + param * D;
    }

    const dx = point.x - xx;
    const dy = point.y - yy;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * 查找从点出发沿方向相交的第一条线段
   * @param point 起始点
   * @param direction 方向向量（不需要归一化）
   * @param segments 线段数组
   * @returns 相交的第一条线段及其交点，如果没有相交则返回null
   */
  static findFirstIntersectingSegment(
    point: IPoint2d,
    direction: IPoint2d,
    segments: LineSeg2d[]
  ): IIntersectInfo | null {
    let closestIntersection: IPoint2d | null = null;
    let closestSegment: LineSeg2d | null = null;
    let minDistance = Infinity;

    // 归一化方向向量
    const dirLength = Math.sqrt(direction.x * direction.x + direction.y * direction.y);
    const normalizedDir = {
      x: direction.x / dirLength,
      y: direction.y / dirLength
    };

    for (const segment of segments) {
      if (segment.pointOnSegment(point)) {
        return null;
      }

      const intersection = Curve2dUtils.getRaySegmentIntersection(point, normalizedDir, segment.start, segment.end);

      if (intersection) {
        // 计算交点距离
        const dx = intersection.x - point.x;
        const dy = intersection.y - point.y;
        const distance = dx * dx + dy * dy; // 使用平方距离比较，避免开方

        if (distance < minDistance) {
          minDistance = distance;
          closestIntersection = intersection;
          closestSegment = segment;
        }
      }
    }

    return closestSegment && closestIntersection
      ? { segment: closestSegment, intersection: closestIntersection }
      : null;
  }

  /**
   * 计算射线与线段的交点
   * @param rayOrigin 射线起点
   * @param rayDir 射线方向（归一化）
   * @param segmentStart 线段起点
   * @param segmentEnd 线段终点
   * @returns 交点或null（如果无交点）
   */
  static getRaySegmentIntersection(
    rayOrigin: IPoint2d,
    rayDir: IPoint2d,
    segmentStart: IPoint2d,
    segmentEnd: IPoint2d
  ): IPoint2d | null {
    // 线段向量
    const segVec = {
      x: segmentEnd.x - segmentStart.x,
      y: segmentEnd.y - segmentStart.y
    };

    // 射线向量和线段向量的叉积
    const cross = rayDir.x * segVec.y - rayDir.y * segVec.x;

    // 如果射线和线段平行或共线，则无交点
    if (Math.abs(cross) < 1e-8) {
      return null;
    }
    // 从射线起点到线段起点的向量
    const originToStart = {
      x: segmentStart.x - rayOrigin.x,
      y: segmentStart.y - rayOrigin.y
    };

    // 计算交点参数
    const t = (originToStart.x * segVec.y - originToStart.y * segVec.x) / cross;
    const u = (originToStart.x * rayDir.y - originToStart.y * rayDir.x) / cross;

    // 检查交点是否在线段上和射线正方向上
    if (u >= 0 && u <= 1 && t >= 0) {
      return {
        x: rayOrigin.x + t * rayDir.x,
        y: rayOrigin.y + t * rayDir.y
      };
    }

    return null;
  }

  /**
   * 合并两个共线且重合的线段
   */
  static mergeSegments(seg1: LineSeg2d, seg2: LineSeg2d): LineSeg2d {
    // 找出所有点
    const points = [seg1.start, seg1.end, seg2.start, seg2.end];

    // 按x坐标排序（如果线段接近水平）或按y坐标排序（如果线段接近垂直）
    const isHorizontal = Math.abs(seg1.end.x - seg1.start.x) > Math.abs(seg1.end.y - seg1.start.y);

    if (isHorizontal) {
      points.sort((a, b) => a.x - b.x);
    } else {
      points.sort((a, b) => a.y - b.y);
    }

    // 返回最远的两个点作为合并后的线段
    return new LineSeg2d(points[0], points[points.length - 1]);
  }

  /**
   * 合并共线且重叠的线段（已知所有线段共线）
   */
  static mergeCollinearOverlappingSegments(segments: LineSeg2d[]): LineSeg2d | undefined {
    if (segments.length === 0) {
      return undefined;
    }

    // 确定线段方向（水平、垂直或斜线）
    const firstSegment = segments[0];
    const isHorizontal = Math.abs(firstSegment.end.y - firstSegment.start.y) < thresholdValue;
    const isVertical = Math.abs(firstSegment.end.x - firstSegment.start.x) < thresholdValue;

    // 收集所有端点
    const points: IPoint2d[] = [];
    segments.forEach((segment) => {
      points.push(segment.start, segment.end);
    });

    // 根据线段方向排序端点
    if (isHorizontal) {
      // 水平线段按x坐标排序
      points.sort((a, b) => a.x - b.x);
    } else if (isVertical) {
      // 垂直线段按y坐标排序
      points.sort((a, b) => a.y - b.y);
    } else {
      // 斜线线段，沿线段方向投影排序
      // 计算线段方向向量
      const dx = firstSegment.end.x - firstSegment.start.x;
      const dy = firstSegment.end.y - firstSegment.start.y;

      // 计算每个点在方向向量上的投影值
      const project = (p: IPoint2d): number => {
        return (p.x * dx + p.y * dy) / (dx * dx + dy * dy);
      };

      // 按投影值排序
      points.sort((a, b) => project(a) - project(b));
    }

    return new LineSeg2d(points[0], points[points.length - 1]);
  }

  static splitCollinearOverlappingSegments(mainSeg: LineSeg2d, segments: LineSeg2d[]): LineSeg2d[] {
    if (segments.length === 0) {
      return [mainSeg];
    }

    let splitSegs = [mainSeg];
    for (const segment of segments) {
      let nonOverlapSegs: LineSeg2d[] = [];
      for (const splitSeg of splitSegs) {
        const overlapSeg = segment.getOverlapSegment(splitSeg);
        if (!overlapSeg) {
          nonOverlapSegs.push(splitSeg);
        } else {
          const nonSegs = segment.getNonOverlapSegments(splitSeg);
          nonOverlapSegs = nonOverlapSegs.concat(nonSegs);
        }
      }

      splitSegs = nonOverlapSegs;
    }

    return splitSegs;
  }

  static segmentIsIntersectLine2d(seg: LineSeg2d, line: Line2d) {
    const mainLine = new Line2d({ points: { p1: seg.start, p2: seg.end } });
    const intersectPoint = mainLine.intersection(line);
    if (intersectPoint) {
      return seg.pointOnSegment(intersectPoint);
    }

    return false;
  }

  static getSegmentIntersectLinePoint(seg: LineSeg2d, line: Line2d) {
    const mainLine = new Line2d({ points: { p1: seg.start, p2: seg.end } });
    const intersectPoint = mainLine.intersection(line);
    if (intersectPoint && seg.pointOnSegment(intersectPoint)) {
      return intersectPoint;
    }

    return undefined;
  }
}

// 测试用例
// const p1 = [
//   { x: 0, y: 0 },
//   { x: 100, y: 0 },
//   { x: 100, y: 100 },
//   { x: 0, y: 100 }
// ];
// const p11 = [
//   { x: 0, y: 0 },
//   { x: 50, y: 50 },
//   { x: 100, y: 0 },
//   { x: 100, y: 100 },
//   { x: 0, y: 100 }
// ];
// const p2 = [
//   { x: 0.1, y: 50 },
//   { x: 50, y: 0.1 },
//   { x: 99.9, y: 50 },
//   { x: 50, y: 99.9 }
// ];
// const p21 = [
//   { x: 10, y: 10 },
//   { x: 90, y: 10 },
//   { x: 90, y: 90 },
//   { x: 10, y: 90 }
// ];

// const p3 = [
//   { x: 0.01, y: 50 },
//   { x: 50, y: 0 },
//   { x: 100, y: 50 },
//   { x: 50, y: 100 }
// ];
// const p4 = [
//   { x: 0, y: 50 },
//   { x: 50, y: 0 },
//   { x: 100, y: 50 },
//   { x: 50, y: 100 }
// ];
// const p5 = [
//   { x: -0.01, y: 50 },
//   { x: 50, y: 0 },
//   { x: 100, y: 50 },
//   { x: 50, y: 100 }
// ];

// console.log('p2 in p1', Curve2dUtils.isPolygonInPolygon(p2, p1) ? 'pass' : 'fail');
// console.log('p3 in p1', Curve2dUtils.isPolygonInPolygon(p3, p1) ? 'pass' : 'fail');
// console.log('p21 in p11', !Curve2dUtils.isPolygonInPolygon(p21, p11) ? 'pass' : 'fail');
// console.log('p4 in p1', Curve2dUtils.isPolygonInPolygon(p4, p1) ? 'pass' : 'fail');
// console.log('p5 in p1', !Curve2dUtils.isPolygonInPolygon(p5, p1) ? 'pass' : 'fail');
